import { createStore, combineReducers, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { routerReducer } from 'react-router-redux';
import { reducer as formReducer } from 'redux-form';

import { config } from '../config/config';
import { version } from '../../package.json';
import {
  handleBodyClasses,
  dumpLayoutToStorage,
  layoutReducer,
} from '../components/layout';
import { apiMiddleware } from 'redux-api-middleware';
import apiAuthInjector from './api-auth-injector';
import apiErrorMiddleware from './api-error';

/* TELEMETRY REDUCERS */
import { currentUserReducer, CurrentUserMiddleware } from './current-user';
import { usersReducer } from './users';
import { rolesReducer } from './roles';
import { labDetailsReducer } from './lab-details';
import { cptLookupReducer } from './cpt-lookup';
import { icdLookupReducer } from './icd-lookup';
import { ndcLookupReducer } from './ndc-lookup';
import { powerBiReducer } from './power-bi';
import { visitNotesReducer } from './visit-notes';
import { locationsReducer } from './locations';
import { organizationsReducer } from './organizations';
import { dashboardReducer } from './dashboard';
import { authReducer } from './auth';
import { locationManagerReducer } from './location-manager';
import { clientManagerReducer } from './client-manager';
import { taskManagerReducer } from './task-manager';
import { eventManagerReducer } from './event-manager';
import { escalationManagerReducer } from './escalation-manager';
import { locationEscalationManagerReducer } from './location-escalation-manager';
import { patientsReducer } from './patients';
import { commentsReducer } from './comments';
import { IMCReducer } from './imc';
import { meetingTypesReducer } from './meeting-types';
import { escalationTypesReducer } from './escalation-types';
import { queryBuilderReducer } from './query-builder';
import { billingManagerReducer } from './billing-manager';
import { clientLocationManagerReducer } from './client-location-manager';
import { tenantReducer } from './tenant';
import { patientDeductibleStatusReducer } from './patient-deductible-status';
import { referralDirectoryReducer } from './referral-directory';
import { contactManagerReducer } from './contact-manager';
import { assetManagerReducer } from './asset-manager';
import { filesReducer } from './files';
import { advocateReducer } from './advocate-dashboard';
import { outreachReducer } from './outreach'
import { clinicalIncidentReducer } from './cir-manager'
import { servicesManagerReducer } from './services-manager'
import { serviceLocationManagerReducer } from './service-location-manager'
import { workLocationManagerReducer } from './work-location-manager'
import { serviceRequestManagerReducer } from './service-request-manager'
import { queryKno2ManagerReducer } from './query-kno2-manager'
import { integrationsReducer } from './integrations'

/* SMART ADMIN REDUCERS */
import { navigationReducer } from './navigation';
import { eventsReducer } from '../components/calendar';
import { voiceReducer, VoiceMiddleware } from '../components/voice-control';
import { chatReducer } from '../components/chat';

import { voiceControlOn } from '../components/voice-control/VoiceActions';

export const rootReducer = combineReducers({
  routing: routerReducer,
  layout: layoutReducer,
  navigation: navigationReducer,
  currentUser: currentUserReducer,
  users: usersReducer,
  roles: rolesReducer,
  organizations: organizationsReducer,
  voice: voiceReducer,
  events: eventsReducer,
  chat: chatReducer,
  visitNotes: visitNotesReducer,
  form: formReducer,
  labdetails: labDetailsReducer,
  cptlookup: cptLookupReducer,
  ndclookup: ndcLookupReducer,
  icdlookup: icdLookupReducer,
  locations: locationsReducer,
  patients: patientsReducer,
  powerbi: powerBiReducer,
  dashboard: dashboardReducer,
  locationmanager: locationManagerReducer,
  clientmanager: clientManagerReducer,
  taskmanager: taskManagerReducer,
  eventmanager: eventManagerReducer,
  escalationmanager: escalationManagerReducer,
  cirmanager: clinicalIncidentReducer,
  auth: authReducer,
  comments: commentsReducer,
  imc: IMCReducer,
  meetingtypes: meetingTypesReducer,
  escalationtypes: escalationTypesReducer,
  queryBuilder: queryBuilderReducer,
  billingmanager: billingManagerReducer,
  clientlocationmanager: clientLocationManagerReducer,
  tenant: tenantReducer,
  patientdeductiblemanager: patientDeductibleStatusReducer,
  locationescalationmanager: locationEscalationManagerReducer,
  referraldirectory: referralDirectoryReducer,
  contactmanager: contactManagerReducer,
  assetmanager: assetManagerReducer,
  servicemanager: servicesManagerReducer,
  servicelocationmanager: serviceLocationManagerReducer,
  servicerequestmanager: serviceRequestManagerReducer,
  worklocationmanager: workLocationManagerReducer,
  files: filesReducer,
  advocates: advocateReducer,
  integrationsmanager: integrationsReducer,
  outreach: outreachReducer,
  querykno2: queryKno2ManagerReducer
});

const persistedState = localStorage.getItem(`reduxState_${version}`)
  ? JSON.parse(localStorage.getItem(`reduxState_${version}`))
  : {};

const store = createStore(
  rootReducer,
  persistedState,
  applyMiddleware(
    thunk,
    handleBodyClasses,
    dumpLayoutToStorage,
    VoiceMiddleware,
    CurrentUserMiddleware,
    apiAuthInjector,
    apiMiddleware,
    apiErrorMiddleware,
  ),
);

store.subscribe(() => {
  const state = store.getState();
  const newState = { currentUser: state.currentUser };
  localStorage.setItem(`reduxState_${version}`, JSON.stringify(newState));
});

if (config.voice_command_auto) {
  store.dispatch(voiceControlOn());
}

export default store;



// WEBPACK FOOTER //
// ./src/store/configureStore.js